import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import type { CompanyApi } from "@/types/api/companies";

/**
 * Company type used in the Settings-Company page UI
 */
export interface CompanySettingsUI {
  id: string;
  name: string;
  status: 'active' | 'inactive';
  logo?: string;
  description?: string;
  integrationStatus: 'connected' | 'partial' | 'none';
  userCount: number;
  createdAt: string;
  // UI convenience aliases for integration linking
  linkedEvoSubscriptionId?: string;
  linkedSubscriptionName?: string;
  linkedProductName?: string;
  // Raw backend fields
  code?: string;
  evoSubscriptionId?: string;
  evoOrganisationId?: string;
  linkedSubscriptionId?: string;
  linkedProductId?: string;
  currencyId?: number;
  taxId?: string;
}

/**
 * Mock companies data for demo mode
 */
const mockCompanies: CompanySettingsUI[] = [
  {
    id: '1',
    name: 'Acme Corporation',
    status: 'active',
    logo: undefined,
    description: 'Leading technology solutions provider',
    integrationStatus: 'none',
    userCount: 24,
    createdAt: '2024-01-15T10:00:00Z',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    status: 'active',
    logo: undefined,
    description: 'Manufacturing and distribution',
    integrationStatus: 'none',
    userCount: 18,
    createdAt: '2024-02-20T14:30:00Z',
  },
  {
    id: '3',
    name: 'Tech Innovations Inc',
    status: 'inactive',
    logo: undefined,
    description: 'Software development company',
    integrationStatus: 'none',
    userCount: 12,
    createdAt: '2024-03-10T09:15:00Z',
  },
  {
    id: '4',
    name: 'Retail Solutions Co',
    status: 'active',
    logo: undefined,
    description: 'Retail management systems',
    integrationStatus: 'none',
    userCount: 31,
    createdAt: '2024-01-05T11:45:00Z',
  },
];

/**
 * Map API response to UI format
 */
function mapCompanyApiToUi(company: CompanyApi): CompanySettingsUI {
  return {
    id: String(company.id),
    name: company.name,
    status: company.status === 'active' ? 'active' : 'inactive',
    logo: undefined,
    description: undefined,
    // Integration status is driven by LinkedSubscriptionId (integration linking), not EvoSubscriptionId (provisioning)
    integrationStatus: company.linkedSubscriptionId ? 'connected' : 'none',
    userCount: 0,
    createdAt: company.createdAt,
    // UI convenience aliases for integration linking
    linkedEvoSubscriptionId: company.linkedSubscriptionId ?? undefined,
    linkedSubscriptionName: company.linkedSubscriptionName ?? undefined,
    linkedProductName: company.linkedProductName ?? undefined,
    // Raw backend fields
    code: company.code ?? undefined,
    evoSubscriptionId: company.evoSubscriptionId ?? undefined,
    evoOrganisationId: company.evoOrganisationId ?? undefined,
    linkedSubscriptionId: company.linkedSubscriptionId ?? undefined,
    linkedProductId: company.linkedProductId ?? undefined,
    currencyId: company.currencyId ?? undefined,
    taxId: company.taxId ?? undefined,
  };
}

/**
 * Hook to fetch companies list data for the Settings-Company page
 * Always uses live mode (API) - Settings Company screen is configured as 'live' only
 */
export function useCompaniesList() {
  const { apiGet } = useApi();

  return useModeAwareQuery<CompanySettingsUI[], Error>(
    ["settings-companies"],
    async ({ signal }) => {
      const response = await apiGet<CompanyApi[]>("/api/v1/companies", undefined, signal);
      return response.map(mapCompanyApiToUi);
    },
    mockCompanies,
    {
      staleTime: 0,
      refetchOnMount: 'always',
      forceLive: true,
    }
  );
}
